/*
  QB-02B — Commercial Intelligence Foundation.

  This module holds the governed commercial profiles that back the Quote
  Builder's recommendation engine. All content here is:

  * PRESENTATION-ONLY starting material — nothing here modifies pricing
    calculations, GST logic, backend schemas, the audit trail, the quote
    lifecycle, the approval workflow or any other governance control.
  * ADVISORY — every field the consultant sees is fully editable and can
    be overwritten, cleared or replaced. Recommendations remain
    recommendations.
  * MODULAR — service profiles and commercial templates are two
    independent, additive data structures. New services or templates can
    be added by extending the maps below with no other changes required.
  * TERMINOLOGY-COMPLIANT — no "override", "certified", "compliant",
    "guarantee", "pass/fail" or "audit outcome" language.

  Deferred to QB-02C / future programmes (per QB-02B brief):
  * AI-generated commercial text
  * LLM-driven document synthesis
  * Automatic issue / send / approve
*/

// ---------- Fields that recommendations may populate ----------
// Fields NOT in this set are NEVER touched by a profile or template:
//   quoteNumber, dateCreated, expiryDate,
//   providerLegalEntity, tradingName, abn, contactPerson, contactEmail,
//   contactPhone, providerSizeOrCohort, urgency,
//   selectedServiceCode (already set by the explicit selection action),
//   pricingBasis, pricingRangeMin, pricingRangeMax
//   (these are governed by the Phase 1A service-change confirmation),
//   proposedFinalPrice, depositAmount, balanceTerms,
//   gstSetting, status, ownerApproved,
//   estimatedHours, clientFacingNotes.
export const RECOMMENDABLE_FIELDS = [
  "assessmentScope",
  "documentReviewScope",
  "estimatedTimeframe",
  "inclusions",
  "exclusions",
  "evidenceRequiredFromProvider",
  "deliverables",
  "additionalProfessionalReferrals",
  "internalCommercialNotes"
];

// Helper — combine a list of bullet strings into a single field value using
// a newline-separated bulleted format. Empty items are ignored. The result
// is plain text (no markdown), so it flows correctly into the existing
// clipboard and preview surfaces without change.
function bullets(items) {
  return items.filter(Boolean).map((s) => `• ${s}`).join("\n");
}

// ---------- Service profiles (keyed by CANONICAL_PRICING service code) ----------
// Each profile is optional per field. Missing fields are simply not
// recommended (the section stays as it was).
export const SERVICE_PROFILES = {
  remote_readiness_triage: {
    label: "Remote Readiness Triage",
    typicalDuration: "1 focused session (approximately 60 minutes) with a short written follow-up",
    recommendedPricingModel: "Fixed engagement — short remote session",
    typicalEffortHours: "1–2 consultant hours end to end",
    suggestedFeePresentation: "Present as a single fixed amount rather than a range where possible.",
    assessmentScope:
      "Short remote review of the provider's current NDIS registration position, immediate SIL readiness pressure points and the most urgent next steps. Not a full evidence review.",
    documentReviewScope:
      "High-level look at any documents the provider has ready. No full document audit at this stage.",
    estimatedTimeframe:
      "Booked within 5–10 business days of acceptance and completed within one week of the session.",
    inclusions: bullets([
      "One remote video session with the owner or nominated contact",
      "Short written summary of key readiness signals and next steps",
      "One follow-up email clarification within 5 business days"
    ]),
    exclusions: bullets([
      "Detailed evidence review or gap report",
      "Policy authoring or rewriting",
      "On-site attendance or travel",
      "Preparation of an application to the NDIS Commission"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Current registration status (registered / unregistered / in-progress)",
      "Brief description of the SIL services currently being delivered",
      "Any recent Commission correspondence the provider is comfortable sharing"
    ]),
    deliverables: bullets([
      "One remote readiness session",
      "One short written readiness summary (not a formal report)",
      "Recommendation on suitable next-stage engagement"
    ]),
    additionalProfessionalReferrals:
      "If the discussion surfaces matters that fall outside NDIS readiness (legal, accounting, taxation, clinical), those will be referred out — the consultancy does not provide legal, accounting or clinical advice.",
    internalCommercialNotes:
      "Recommendation-only starting content. Confirm quote number, date, expiry and the exact final fee before sending. Consider whether the provider actually needs a full Audit-Readiness Review as the follow-on."
  },
  audit_readiness_review: {
    label: "Audit-Readiness Review",
    typicalDuration: "2–4 weeks from receipt of the complete evidence pack",
    recommendedPricingModel:
      "Fixed engagement. Confirm scope before quoting toward the upper end of the range.",
    typicalEffortHours: "12–24 consultant hours depending on scope",
    suggestedFeePresentation:
      "Quote a range initially, then confirm a single final fee once scope is agreed with the owner.",
    assessmentScope:
      "Structured readiness review against the R.E.A.D.Y. framework and current SIL-specific practice-standards signals. Consultancy readiness review only — not an Approved Quality Auditor engagement.",
    documentReviewScope:
      "Review of the provider's current policies, procedures, evidence pack, incident register and training records within the agreed scope.",
    estimatedTimeframe:
      "2–4 weeks from receipt of a complete evidence pack. Provider evidence delays extend the timeframe accordingly.",
    inclusions: bullets([
      "Structured readiness review against the R.E.A.D.Y. framework",
      "Evidence readiness table with per-item findings",
      "Missing-evidence and evidence-needing-review lists",
      "30 / 60 / 90 day action plan",
      "One debrief call with the owner"
    ]),
    exclusions: bullets([
      "Preparation or lodgement of applications to the NDIS Commission",
      "Interviews with participants or their nominees",
      "Policy or procedure rewriting (available as a separate engagement)",
      "On-site attendance unless separately quoted",
      "Ongoing implementation support after the review"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Current policies and procedures relevant to SIL",
      "Recent incident register (de-identified where necessary)",
      "Staff training and induction records",
      "Any existing readiness or self-assessment material",
      "Latest Commission correspondence"
    ]),
    deliverables: bullets([
      "Written readiness report (electronic delivery)",
      "Evidence readiness table",
      "30 / 60 / 90 day action plan",
      "One structured debrief call with the owner"
    ]),
    additionalProfessionalReferrals:
      "Where the review surfaces matters outside NDIS readiness — such as legal, accounting, taxation or clinical governance — those are noted as external professional referrals rather than covered by this engagement.",
    internalCommercialNotes:
      "Recommendation-only starting content. If the provider has more than one site, or if a significant portion of policies need rewriting, consider quoting toward the upper range. Confirm the final fee before sending."
  },
  gap_report: {
    label: "Gap Report",
    typicalDuration: "3–5 weeks from receipt of the complete evidence pack",
    recommendedPricingModel: "Fixed engagement",
    typicalEffortHours: "16–32 consultant hours depending on scope",
    suggestedFeePresentation:
      "Quote a range initially and confirm a single final fee once scope is agreed.",
    assessmentScope:
      "Written gap analysis identifying evidence readiness gaps against the R.E.A.D.Y. framework and current SIL-specific practice-standards signals. Focuses on the delta between current and expected readiness rather than a general audit rehearsal.",
    documentReviewScope:
      "Targeted review of the specific documents required to identify readiness gaps within the agreed scope.",
    estimatedTimeframe:
      "3–5 weeks from receipt of a complete evidence pack. Provider evidence delays extend the timeframe accordingly.",
    inclusions: bullets([
      "Gap-focused readiness review",
      "Structured written gap report",
      "Prioritised remediation list",
      "One debrief call with the owner"
    ]),
    exclusions: bullets([
      "Rewriting of the identified gap-fixing documents",
      "Implementation of the remediation list",
      "Interviews with participants or their nominees",
      "Preparation of Commission applications"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Current policies and procedures",
      "Existing readiness or self-assessment work if any",
      "Any recent internal reviews or feedback letters"
    ]),
    deliverables: bullets([
      "Written gap report (electronic delivery)",
      "Prioritised remediation list",
      "One structured debrief call with the owner"
    ]),
    additionalProfessionalReferrals:
      "Matters outside NDIS readiness (legal, accounting, taxation, clinical) will be referred out — this engagement does not provide legal, accounting or clinical advice.",
    internalCommercialNotes:
      "Recommendation-only starting content. If the provider expects the report to also include drafted remediation documents, that is a separate engagement — flag before sending the quote."
  },
  done_for_you_evidence_pack: {
    label: "Done-for-You Evidence Pack",
    typicalDuration: "6–12 weeks depending on scope",
    recommendedPricingModel:
      "Fixed engagement. Upper end is not capped for larger scopes — confirm scope with the owner before sending toward the upper range.",
    typicalEffortHours: "40+ consultant hours depending on scope",
    suggestedFeePresentation:
      "Present the range initially. Confirm a single final fee before sending, after scope is agreed with the owner.",
    assessmentScope:
      "End-to-end preparation of a de-identified evidence pack aligned with the R.E.A.D.Y. framework and current SIL-specific practice-standards signals. Consultancy readiness preparation only — not an audit determination.",
    documentReviewScope:
      "Full document set for the SIL services being prepared, plus any supporting operational documents required to complete the evidence pack.",
    estimatedTimeframe:
      "6–12 weeks from receipt of the complete provider input, depending on scope, number of sites and the volume of documents to be authored or refreshed.",
    inclusions: bullets([
      "Structured evidence pack aligned to the R.E.A.D.Y. framework",
      "Authoring or refresh of the required supporting documents within the agreed scope",
      "Provider-facing checklists and readiness summary",
      "Progress meetings with the owner during the engagement",
      "One structured handover session at the end of the engagement"
    ]),
    exclusions: bullets([
      "Lodgement of applications to the NDIS Commission on the provider's behalf",
      "Recruitment, HR or workforce implementation",
      "Software procurement or implementation",
      "On-site attendance unless separately quoted"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Access to the provider's existing policies, procedures and forms",
      "Timely responses to information requests during the engagement",
      "A single nominated internal owner for the engagement"
    ]),
    deliverables: bullets([
      "Structured evidence pack (electronic delivery)",
      "Supporting documents authored or refreshed within scope",
      "Provider-facing readiness summary and checklists",
      "One structured handover session"
    ]),
    additionalProfessionalReferrals:
      "Legal, accounting, taxation, clinical governance and lease / property matters are outside scope and will be referred to appropriate external professionals.",
    internalCommercialNotes:
      "Recommendation-only starting content. Larger scopes push toward the upper range or beyond — confirm scope, sites, document count and required turnaround before sending."
  },
  exit_transition_plan: {
    label: "EXIT Transition Plan",
    typicalDuration: "3–6 weeks depending on the number of participants and services",
    recommendedPricingModel: "Fixed engagement",
    typicalEffortHours: "16–40 consultant hours depending on scope",
    suggestedFeePresentation:
      "Quote a range initially and confirm a single final fee once scope is agreed.",
    assessmentScope:
      "Structured written plan for a provider who is exiting or transitioning out of SIL delivery. Focuses on continuity of supports, communication with participants and their nominees, and orderly notification to the NDIS Commission consistent with the 'Stopping the services you provide' guidance.",
    documentReviewScope:
      "Current participant support plans, service agreements, staffing arrangements and recent Commission correspondence relevant to the exit.",
    estimatedTimeframe:
      "3–6 weeks from acceptance depending on the number of participants and services being transitioned.",
    inclusions: bullets([
      "Structured written exit / transition plan",
      "Timeline aligned with Commission stopping-services guidance",
      "Participant and nominee communication templates",
      "One debrief call with the owner"
    ]),
    exclusions: bullets([
      "Direct communication with participants or their nominees on the provider's behalf",
      "Recruitment or replacement of a receiving provider",
      "Legal, accounting or taxation advice on the exit"
    ]),
    evidenceRequiredFromProvider: bullets([
      "List of participants and services being transitioned (de-identified where necessary)",
      "Any prior communication with participants or their nominees about the transition",
      "Recent Commission correspondence about the exit"
    ]),
    deliverables: bullets([
      "Written exit / transition plan",
      "Communication templates",
      "One structured debrief call with the owner"
    ]),
    additionalProfessionalReferrals:
      "Legal, accounting, taxation, clinical and property matters relevant to the exit are outside scope and will be referred out.",
    internalCommercialNotes:
      "Recommendation-only starting content. Check whether the provider expects the consultancy to communicate with participants directly — that is excluded and must be a separate engagement."
  },
  urgent_must_registration_triage: {
    label: "Urgent MUST Registration Triage",
    typicalDuration: "1–3 weeks — expedited scheduling where possible",
    recommendedPricingModel:
      "Fixed engagement — urgent handling. Confirm capacity before accepting.",
    typicalEffortHours: "12–40 consultant hours depending on urgency and scope",
    suggestedFeePresentation:
      "Quote toward the upper end where the urgency is genuine and where scheduling pressure applies.",
    assessmentScope:
      "Urgent triage for a provider approaching the 1 October 2026 unregistered-SIL application deadline. Focuses on whether an application can realistically be prepared in the available time and, if so, what the highest-priority readiness signals are.",
    documentReviewScope:
      "Rapid look at the documents the provider already has, prioritised by what will most affect application readiness.",
    estimatedTimeframe:
      "Expedited scheduling within 5 business days of acceptance where consultant capacity allows.",
    inclusions: bullets([
      "Urgent triage session with the owner",
      "Written triage summary with prioritised next steps",
      "One follow-up call within the engagement window"
    ]),
    exclusions: bullets([
      "Lodgement of the application on the provider's behalf",
      "Full evidence pack preparation (available as a separate engagement)",
      "Any commitment about the outcome of a future Commission decision"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Current registration position and any prior Commission contact",
      "The SIL services currently being delivered",
      "The provider's own view of the most critical readiness gaps"
    ]),
    deliverables: bullets([
      "Urgent triage session",
      "Written triage summary with prioritised next steps"
    ]),
    additionalProfessionalReferrals:
      "Legal, accounting, taxation and clinical matters are outside scope and will be referred out where they arise.",
    internalCommercialNotes:
      "Recommendation-only starting content. Verify consultant capacity before accepting. Be explicit with the client that this engagement does not guarantee any specific Commission outcome."
  },
  consulting_standard: {
    label: "Standard consulting",
    typicalDuration: "Booked in blocks (typically 1–4 hours at a time)",
    recommendedPricingModel:
      "Hourly. Confirm the estimated block of hours with the owner before booking.",
    typicalEffortHours: "1–8 consultant hours per booking, agreed in advance",
    suggestedFeePresentation:
      "Quote as an hourly rate with an agreed estimated block of hours (e.g. 4 hours at the standard rate).",
    assessmentScope:
      "General consultancy support on NDIS-readiness matters within scope. Time-boxed to the agreed block of hours.",
    documentReviewScope:
      "Any documents the provider brings to the session or shares in advance, within the agreed hours.",
    estimatedTimeframe:
      "Booked within 5–10 business days of acceptance and completed within the agreed block of hours.",
    inclusions: bullets([
      "Consultant time within the agreed block of hours",
      "Short written summary of the session (email length, not a formal report)",
      "One follow-up email clarification within 5 business days of the session"
    ]),
    exclusions: bullets([
      "Preparation of a formal written readiness report",
      "Policy or procedure rewriting unless separately booked",
      "Preparation of Commission applications"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Any documents the provider wants reviewed",
      "A short written summary of the question or topic before the session"
    ]),
    deliverables: bullets([
      "Consultant time within the agreed hours",
      "Short written session summary (email length)"
    ]),
    additionalProfessionalReferrals:
      "Legal, accounting, taxation and clinical matters are outside scope and will be referred out where they arise.",
    internalCommercialNotes:
      "Recommendation-only starting content. Set the agreed block of hours before booking and confirm the hourly rate + GST treatment on the invoice."
  },
  consulting_urgent: {
    label: "Urgent or complex consulting",
    typicalDuration: "Booked in blocks (typically 1–4 hours) with expedited scheduling",
    recommendedPricingModel:
      "Hourly (urgent / complex rate). Confirm the estimated block of hours with the owner before booking.",
    typicalEffortHours: "1–8 consultant hours per booking, agreed in advance",
    suggestedFeePresentation:
      "Quote as an urgent hourly rate with an agreed estimated block of hours. Be explicit that the higher rate reflects urgency or complexity.",
    assessmentScope:
      "General consultancy support on urgent or complex NDIS-readiness matters within scope. Time-boxed to the agreed block of hours.",
    documentReviewScope:
      "Any documents the provider brings to the session or shares in advance, within the agreed hours.",
    estimatedTimeframe:
      "Expedited scheduling where consultant capacity allows.",
    inclusions: bullets([
      "Consultant time within the agreed block of hours (urgent scheduling)",
      "Short written summary of the session (email length)",
      "One follow-up email clarification within 3 business days of the session"
    ]),
    exclusions: bullets([
      "Preparation of a formal written readiness report",
      "Policy or procedure rewriting unless separately booked",
      "Preparation of Commission applications",
      "Any commitment about a future Commission outcome"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Any documents the provider wants reviewed",
      "A short written summary of the urgent question or issue before the session"
    ]),
    deliverables: bullets([
      "Consultant time within the agreed hours (urgent scheduling)",
      "Short written session summary (email length)"
    ]),
    additionalProfessionalReferrals:
      "Legal, accounting, taxation and clinical matters are outside scope and will be referred out where they arise.",
    internalCommercialNotes:
      "Recommendation-only starting content. Verify consultant capacity before accepting. Confirm the urgent hourly rate and any GST treatment on the invoice before booking."
  }
};

// ---------- Commercial templates (independent of service selection) ----------
// A template is a smaller, focused overlay the consultant can apply on top
// of a service profile (or on its own). Templates never touch pricing,
// service code or provider details.
export const COMMERCIAL_TEMPLATES = {
  remote_readiness_review: {
    label: "Remote Readiness Review",
    description: "Adds remote-only delivery signals to inclusions and exclusions.",
    inclusions: bullets([
      "All meetings by secure video conference",
      "All document review conducted remotely",
      "Session notes shared electronically only"
    ]),
    exclusions: bullets([
      "On-site attendance",
      "Travel time and expenses",
      "Physical delivery of paper documents"
    ])
  },
  desktop_evidence_review: {
    label: "Desktop Evidence Review",
    description: "Adds desktop-review framing to scope, inclusions and evidence required.",
    assessmentScope:
      "Desktop review of the provider's evidence pack against the R.E.A.D.Y. framework. No live interviews, workshops or observation.",
    inclusions: bullets([
      "Structured desktop review of the evidence pack",
      "Written findings against the R.E.A.D.Y. framework"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Complete evidence pack in electronic form before the review commences",
      "One nominated point of contact for evidence clarifications"
    ])
  },
  gap_analysis: {
    label: "Gap Analysis",
    description: "Adds gap-focused framing to scope and deliverables.",
    assessmentScope:
      "Structured gap analysis of the provider's current readiness against the R.E.A.D.Y. framework. Focused on the delta between current and expected readiness.",
    deliverables: bullets([
      "Written gap analysis",
      "Prioritised remediation list",
      "One debrief call with the owner"
    ])
  },
  policy_review: {
    label: "Policy Review",
    description: "Adds policy-review framing to scope, inclusions and exclusions.",
    assessmentScope:
      "Structured review of a nominated set of policies and procedures relevant to SIL delivery. Findings-only — not policy rewriting.",
    inclusions: bullets([
      "Structured review of the nominated policies and procedures",
      "Written findings against the R.E.A.D.Y. framework",
      "Recommendations for prioritised remediation"
    ]),
    exclusions: bullets([
      "Rewriting of the reviewed policies or procedures",
      "Legal review of policy wording"
    ])
  },
  operational_readiness: {
    label: "Operational Readiness",
    description: "Adds operational-readiness framing to scope, inclusions and evidence required.",
    assessmentScope:
      "Structured review of operational readiness — incident management, training, rostering, safeguarding practices, complaints handling and other day-to-day systems — against the R.E.A.D.Y. framework.",
    inclusions: bullets([
      "Operational-readiness review across incident, training, rostering, safeguarding and complaints",
      "Written findings and prioritised remediation list"
    ]),
    evidenceRequiredFromProvider: bullets([
      "Recent incident register (de-identified where necessary)",
      "Staff training and induction records",
      "Recent rostering samples",
      "Complaints register (de-identified where necessary)"
    ])
  },
  on_site_consultancy: {
    label: "On-site Consultancy",
    description: "Adds on-site delivery signals to scope, inclusions and exclusions.",
    assessmentScope:
      "Consultancy delivered on-site at a nominated provider location. Includes structured observation of operational practice within the agreed scope.",
    inclusions: bullets([
      "One or more on-site consultant days at the nominated location",
      "Structured observation notes",
      "Written summary of on-site findings"
    ]),
    exclusions: bullets([
      "Travel time and expenses beyond a nominated radius (quoted separately)",
      "Direct participant interviews unless expressly quoted and consented",
      "Overnight accommodation unless expressly quoted"
    ])
  },
  audit_preparation: {
    label: "Audit Preparation",
    description: "Adds audit-preparation framing. Not an audit determination.",
    assessmentScope:
      "Consultancy readiness preparation ahead of an Approved Quality Auditor engagement. Does not include the audit itself and does not provide an audit determination.",
    inclusions: bullets([
      "Structured readiness rehearsal against the R.E.A.D.Y. framework",
      "Evidence-pack review with prioritised remediation",
      "One structured owner debrief before the audit"
    ]),
    exclusions: bullets([
      "The audit engagement itself (that is undertaken by an Approved Quality Auditor)",
      "Any commitment about the outcome of the audit",
      "Direct interaction with the auditor on the provider's behalf"
    ])
  }
};

// ---------- Consultant prompts (session-only decision support) ----------
// These are checklist items surfaced next to the Quote Builder. They do
// not persist to the backend (per the QB-02B "prompts only" rule) — they
// exist to help the consultant remember commercial questions before
// sending a quote.
export const CONSULTANT_PROMPTS = [
  { id: "evidence_discussed", text: "Have the evidence requirements been discussed with the provider?" },
  { id: "travel", text: "Does this quotation require travel or on-site attendance?" },
  { id: "followup", text: "Is follow-up consultancy recommended after this engagement?" },
  { id: "implementation", text: "Should implementation assistance be offered as a separate engagement?" },
  { id: "referrals", text: "Have any external professional referrals (legal, accounting, clinical) been noted?" },
  { id: "commission_deadline", text: "Is the 1 October 2026 unregistered-SIL deadline relevant to this provider?" }
];

export function getServiceProfile(code) {
  if (!code) return null;
  return SERVICE_PROFILES[code] || null;
}

export function getCommercialTemplate(id) {
  if (!id) return null;
  return COMMERCIAL_TEMPLATES[id] || null;
}

// applyRecommendationToDraft
// -----------------------------------------------------------------
// Merges the recommendation values from a profile or template into
// the current draft, respecting `touchedFields` (a Set of field keys
// the consultant has authored / edited manually).
//
// Behaviour rules (governance-aligned):
//   * ONLY fields in RECOMMENDABLE_FIELDS may be affected.
//   * A field the consultant has already touched is NEVER
//     overwritten, unless `forceOverwrite = true` is passed
//     (used only when the consultant explicitly chooses the
//     "regenerate commercial content" option in the dialog).
//   * A field with an existing non-empty value that was NOT
//     touched (e.g. previously set by an earlier recommendation)
//     IS refreshed with the new recommendation, so switching
//     between services updates the generated starting content
//     rather than stacking older values.
//   * Fields for which the recommendation has no value are left
//     alone.
//
// Returns:
//   { nextDraft, appliedFields, skippedFields }
export function applyRecommendationToDraft(
  draft,
  recommendation,
  { touchedFields = new Set(), forceOverwrite = false } = {}
) {
  if (!recommendation) {
    return { nextDraft: draft, appliedFields: [], skippedFields: [] };
  }
  const nextDraft = { ...draft };
  const appliedFields = [];
  const skippedFields = [];
  for (const field of RECOMMENDABLE_FIELDS) {
    const value = recommendation[field];
    if (value === undefined || value === null || value === "") continue;
    const isTouched = touchedFields.has(field);
    if (isTouched && !forceOverwrite) {
      skippedFields.push(field);
      continue;
    }
    nextDraft[field] = value;
    appliedFields.push(field);
  }
  return { nextDraft, appliedFields, skippedFields };
}

// Human-readable label for a recommendable field. Kept here so any
// future field additions stay in one place.
export const RECOMMENDABLE_FIELD_LABELS = {
  assessmentScope: "Assessment scope",
  documentReviewScope: "Document review scope",
  estimatedTimeframe: "Estimated timeframe",
  inclusions: "Inclusions",
  exclusions: "Exclusions",
  evidenceRequiredFromProvider: "Evidence required from provider",
  deliverables: "Deliverables",
  additionalProfessionalReferrals: "Additional professional referrals",
  internalCommercialNotes: "Internal commercial notes"
};
